import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Eye, EyeOff, Handshake } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useFeatureFlagsStore } from '@/stores/featureFlagsStore';

/**
 * FeatureFlagsCard (iter593)
 *
 * Compact UI for per-tenant feature toggles. Currently exposes:
 *   - enable_partner_entries: hide / show the "Partner" option in
 *     Multi-Person Expense dialog, Banking Hub linker, etc.
 *
 * Mirrored from the Partners page banner so admins can manage the
 * same flag from Settings without leaving the area they're already in.
 */
export default function FeatureFlagsCard() {
  const partnersEnabled = useFeatureFlagsStore(s => s.flags.enable_partner_entries);
  const setFlag = useFeatureFlagsStore(s => s.setFlag);
  const [busy, setBusy] = useState(false);

  const toggle = async (val) => {
    // iter594 — Turning ON is code-gated. OFF is always free.
    let extra = {};
    if (val) {
      const code = window.prompt('Enter unlock code to enable Partner entries:');
      if (code === null) return;
      if (!code) { toast.error('Unlock code is required'); return; }
      extra = { unlock_code: code };
    }
    setBusy(true);
    try {
      await setFlag('enable_partner_entries', val, extra);
      toast.success(val ? 'Partner entries enabled' : 'Partner entries hidden');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-stone-300" data-testid="feature-flags-card">
      <CardContent className="p-4 sm:p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Handshake size={16} className="text-stone-600" />
          <h3 className="text-sm font-semibold">Module visibility</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          Hide modules you don&rsquo;t use anywhere in the app. Existing data is preserved either way — these toggles only affect what NEW entries can be tagged as.
        </p>

        <div className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${partnersEnabled ? 'border-emerald-200 bg-emerald-50/40 dark:bg-emerald-950/10' : 'border-stone-200 bg-stone-50/60 dark:bg-stone-900/30'}`}>
          <div className="flex items-start gap-2.5 flex-1 min-w-0">
            <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${partnersEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-600'}`}>
              {partnersEnabled ? <Eye size={14} /> : <EyeOff size={14} />}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium" data-testid="settings-partner-flag-headline">
                Partner entries in expenses & banking
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {partnersEnabled
                  ? 'ON — "Partner" option is visible in Multi-Person Expense and Banking Hub linker.'
                  : 'OFF — Locked. Enabling requires the admin unlock code.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Switch
              checked={partnersEnabled}
              disabled={busy}
              onCheckedChange={toggle}
              data-testid="settings-toggle-partner-entries"
            />
            <span className="text-xs font-medium select-none w-7 text-right">{partnersEnabled ? 'ON' : 'OFF'}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
